import { z } from "zod";

/**
 * Parses env-var strings into booleans correctly.
 *
 * `z.coerce.boolean()` uses `Boolean(value)` which treats the string "false"
 * as truthy. This schema only accepts known boolean representations and throws
 * on anything else (catches typos like "ture" early).
 *
 * Accepts: boolean | "true" | "1" | "false" | "0"
 * Callers must chain `.default()` to handle undefined values.
 */
export const booleanEnv = z
  .union([z.boolean(), z.string()])
  .transform((val: boolean | string) => {
    if (typeof val === "boolean") return val;
    const lower = val.trim().toLowerCase();
    if (lower === "true" || lower === "1") return true;
    if (lower === "false" || lower === "0") return false;
    throw new Error(`Invalid boolean env value: "${val}" (expected "true", "false", "1", or "0")`);
  });

export const nodeEnvSchema = z.enum(["development", "production", "test"]).default("development");

const emptyStringAsUndefined = (value: unknown): unknown =>
  typeof value === "string" && value.trim() === "" ? undefined : value;

export const authFragment = {
  BETTER_AUTH_SECRET: z.string().min(1),
  BETTER_AUTH_TRUSTED_ORIGINS: z.string().optional(),
  JWT_SIGNING_KEY: z.string().min(1).optional(),
};

export const metabaseFragment = {
  METABASE_URL: z.string().url(),
  METABASE_SECRET: z.string().min(1),
};

export const azureFragment = {
  AZURE_OPENAI_RESOURCE_NAME: z.string().min(1),
  AZURE_OPENAI_API_KEY: z.string().min(1),
  AZURE_OPENAI_DEPLOYMENT: z.string().min(1).default("gpt-4.1"),
  AZURE_OPENAI_DEPLOYMENT_MINI: z.string().min(1).default("gpt-4.1-mini"),
  AZURE_OPENAI_EMBEDDING_DEPLOYMENT: z.string().min(1).default("text-embedding-3-small"),
  AZURE_OPENAI_EMBEDDING_DIMENSIONS: z.coerce.number().int().positive().default(512),
};

export const aiProviderFragment = {
  OPENAI_API_KEY: z.string().optional(),
  OPENAI_MODEL: z.string().default("gpt-4.1"),
  GOOGLE_GENERATIVE_AI_API_KEY: z.string().optional(),
  GOOGLE_GENERATIVE_AI_MODEL: z.string().default("gemini-2.5-flash"),
};

export const cohereFragment = {
  COHERE_API_KEY: z.string().optional(),
  COHERE_RERANK_MODEL: z.string().default("rerank-v3.5"),
  // Chunks handed to the model per search. Kept wide enough that a single
  // search can answer a multi-entity question ("who works in department X").
  COHERE_RERANK_TOP_N: z.coerce.number().int().positive().default(10),
  COHERE_RERANK_RELEVANCE_FILTER: z.coerce.number().min(0).max(1).default(0.15),
};

export const turbopufferFragment = {
  TURBOPUFFER_API_KEY: z.string().min(1),
  TURBOPUFFER_ENV: z.string().min(1),
  TURBOPUFFER_REGION: z.string().min(1).default("gcp-europe-west3"),
  // Candidate pool per retrieval arm. The reranker is what narrows this down,
  // so the pool must be several times wider than COHERE_RERANK_TOP_N.
  TURBOPUFFER_VECTOR_SEARCH_TOP_K: z.coerce.number().int().positive().default(20),
  TURBOPUFFER_BM25_SEARCH_TOP_K: z.coerce.number().int().positive().default(20),
};

export const langsmithFragment = {
  LANGSMITH_TRACING: booleanEnv.default(false),
  LANGSMITH_API_KEY: z.string().optional(),
  LANGSMITH_PROJECT: z.string().default("humanizing-chatapi"),
  LANGSMITH_ENDPOINT: z.string().default("https://api.smith.langchain.com"),
};

export const convexFragment = {
  CONVEX_URL: z.string().url().optional(),
  NEXT_PUBLIC_CONVEX_URL: z.string().url(),
  NEXT_PUBLIC_CONVEX_SITE_URL: z.string().min(1).optional(),
  CONVEX_SITE_URL: z.string().min(1).optional(),
  CONVEX_ADMIN_KEY: z.string().optional(),
  CONVEX_WEBHOOK_SECRET: z.string().min(1),
};

export const twilioFragment = {
  TWILIO_ACCOUNT_SID: z.string().min(1),
  TWILIO_AUTH_TOKEN: z.string().min(1),
  TWILIO_API_KEY: z.string().min(1),
  TWILIO_API_SECRET: z.string().min(1),
  TWILIO_TWIML_APP_SID: z.string().min(1),
  TWILIO_PHONE_NUMBER: z.string().min(1),
  TWILIO_SERVICE_URL: z.string().url(),
  TWILIO_SERVICE_SHARED_SECRET: z.string().min(1).optional(),
};

export const posthogFragment = {
  NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
  NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),
};

export const posthogServerFragment = {
  POSTHOG_API_KEY: z.string().optional(),
  POSTHOG_KEY: z.string().optional(),
  // Ingest host for POSTHOG_KEY. Optional; consumers default to the EU cloud
  // region, which is where the Humanizing / Cosmo project lives. `.env.local`
  // files carry the key with an empty value, so a blank string means unset
  // rather than an invalid URL.
  POSTHOG_HOST: z.preprocess(emptyStringAsUndefined, z.string().url().optional()),
};

/**
 * Optional env vars for the agents app.
 *
 * NOTE: TWILIO_SERVICE_URL, TWILIO_SERVICE_BYPASS_SECRET, and
 * TWILIO_SERVICE_SHARED_SECRET overlap with `twilioFragment` but differ in
 * optionality (optional here, mostly required there). Both fragments are needed
 * because agents and twilio-service are separate consumers — agents only
 * optionally calls twilio-service, while twilio-service requires its own config.
 */
export const agentOptionalFragment = {
  DEFAULT_AVATAR_ID: z.string().optional(),
  ADMIN_EMAILS: z
    .string()
    .transform((val) => val.split(",").map((email) => email.trim()).filter(Boolean))
    .optional(),
  CRON_SECRET: z.string().min(1).optional(),
  FIRECRAWL_API_KEY: z.string().min(1),
  RESEND_API_KEY: z.string().min(1),
  RESEND_TMPL_ADMIN_WS_INVITATION: z.string().min(1).optional(),
  RESEND_TMPL_ADMIN_WS_INVITE_REMINDER: z.string().min(1).optional(),
  RESEND_TMPL_ADMIN_MEMBER_WELCOME: z.string().min(1).optional(),
  RESEND_TMPL_CONTACT_FORM: z.string().min(1).optional(),
  RESEND_TMPL_POST_CALL_SUMMARY: z.string().min(1).optional(),
  TRIGGER_SECRET_KEY: z.string().min(1),
  TRIGGER_PREVIEW_BRANCH: z.string().min(1).optional(),
  TWILIO_SERVICE_URL: z.string().url().optional(),
  TWILIO_SERVICE_BYPASS_SECRET: z.string().min(1).optional(),
  TWILIO_SERVICE_SHARED_SECRET: z.string().min(1).optional(),
  VOICEBOT_PUBLIC_URL: z.string().url().optional(),
};

/**
 * Reusable schema for "optional comma-separated list" env vars.
 * Missing/empty yields `[]`. Never a permissive fallback — caller must check
 * `length === 0` if it cares.
 */
export const commaSeparatedListSchema = z
  .string()
  .optional()
  .transform((val) =>
    (val ?? "")
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean),
  );

/**
 * HT-566 ElevenLabs phone-number import env vars.
 * The DEV list contains E.164 strings; parsing is intentionally permissive (every
 * non-empty trimmed entry is kept) because the importer treats the lists as
 * membership oracles, not as a phone-number validator.
 */
export const elevenlabsPhoneImportFragment = {
  ELEVENLABS_DEV_PHONE_NUMBERS: commaSeparatedListSchema,
};

/**
 * Vercel-injected deployment-environment discriminator.
 * In a Vercel-hosted Next.js app, `VERCEL_ENV` is set by the platform on every
 * deploy. Local `bun dev` and tests fall through to `'development'`.
 */
export const vercelEnvFragment = {
  VERCEL_ENV: z
    .enum(["development", "preview", "production"])
    .default("development"),
};

export const publicVercelEnvFragment = {
  NEXT_PUBLIC_VERCEL_ENV: z
    .enum(["development", "preview", "production"])
    .default("development"),
};

export const elevenlabsAgentFragment = {
  ELEVENLABS_ADMIN_KEY: z.string().min(1),
  ELEVENLABS_WEBHOOK_SECRET: z.string().min(1).optional(),
};

export const clickhouseFragment = {
  CLICKHOUSE_URL: z.string().url().optional(),
  CLICKHOUSE_USER: z.string().default("default"),
  CLICKHOUSE_PASSWORD: z.string().optional(),
};

export const logAndLLMFragment = {
  LOG_LEVEL: z.enum(["debug", "info", "warn", "error"]).default("info"),
  LLM_TEMPERATURE: z.coerce.number().min(0).max(2).default(0.3),
};

export const cosmoClickhouseFragment = {
  COSMO_CLICKHOUSE_ENABLED: booleanEnv.default(false),
  COSMO_CLICKHOUSE_URL: z.preprocess(
    emptyStringAsUndefined,
    z.string().optional(),
  ),
  COSMO_CLICKHOUSE_USER: z.preprocess(
    emptyStringAsUndefined,
    z.string().min(1).optional(),
  ),
  COSMO_CLICKHOUSE_PASSWORD: z.preprocess(
    emptyStringAsUndefined,
    z.string().min(1).optional(),
  ),
  COSMO_CLICKHOUSE_DATABASE: z.preprocess(
    emptyStringAsUndefined,
    z.string().optional(),
  ),
};

export const cosmoClickhouseReaderFragment = {
  COSMO_CONVERSATION_READER: z.enum(["convex", "clickhouse"]).default("convex"),
  COSMO_CLICKHOUSE_READER_URL: z.preprocess(
    emptyStringAsUndefined,
    z.string().url().optional(),
  ),
  COSMO_CLICKHOUSE_READER_USER: z.preprocess(
    emptyStringAsUndefined,
    z.string().min(1).optional(),
  ),
  COSMO_CLICKHOUSE_READER_PASSWORD: z.preprocess(
    emptyStringAsUndefined,
    z.string().min(1).optional(),
  ),
  COSMO_CLICKHOUSE_READER_DATABASE: z.preprocess(
    emptyStringAsUndefined,
    z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/).optional(),
  ),
};

export const wasteCalendarFragment = {
  WASTE_CALENDAR_ENABLED: z
    .preprocess(emptyStringAsUndefined, z.enum(["true", "false"]).default("false"))
    .transform((value) => value === "true"),
  WASTE_CALENDAR_SUPABASE_URL: z.preprocess(
    emptyStringAsUndefined,
    z.string().url().optional(),
  ),
  WASTE_CALENDAR_SUPABASE_API_KEY: z.preprocess(
    emptyStringAsUndefined,
    z.string().min(1).optional(),
  ),
  WASTE_CALENDAR_RPC_NAME: z.preprocess(
    emptyStringAsUndefined,
    z.string().min(1).default("waste_get_next_pickups"),
  ),
};

export const stripeFragment = {
  STRIPE_SECRET_KEY: z.string().min(1),
  STRIPE_WEBHOOK_SECRET: z.string().min(1),
  STRIPE_METER_ID: z.string().min(1),
  STRIPE_PUBLISHABLE_KEY: z.string().min(1).optional(),
};

export const pluralFetcherFragment = {
  PLURAL_SYSADMIN_EMAIL: z.string().email().optional(),
  PLURAL_SYSADMIN_PASSWORD: z.string().min(1).optional(),
  PLURAL_SYSADMIN_ORGANISATION_ID: z.string().min(1).optional(),
  PLURAL_API_BASE_URL: z
    .string()
    .url()
    .refine((u) => {
      try {
        return new URL(u).protocol === "https:";
      } catch {
        return false;
      }
    }, "PLURAL_API_BASE_URL must use https")
    .default("https://go.plural.io"),
};
